/*!
 * relative.js — a value relative to what it is now: rate:*0.9, pan:+=0.2
 * © 2026 Rob Canning — GPLv3
 *
 *     say(agents, rate:*0.9)        each voice 10% slower than it is
 *     say(a1, pan:+=0.2)            nudged right; pan:-=0.2 left
 *     level(agents, *0.5, 4)        each fader to half where it is, over 4 beats
 *     synth(uid:s1, freq:*1.5)      a fifth up from where it is
 *
 * The parser reads *k, *=k, +=k, -=k as { type: "rel", op, by }. Each cue
 * that knows what a value is now applies it (applyRel); where nothing is
 * there yet it counts from the value's own default.
 */

/** Whether `v` is a relative value. */
export const isRel = v => v != null && typeof v === "object" && v.type === "rel";

/** `v` applied to `current` -- a plain value is itself. */
export function applyRel(current, v) {
  if (!isRel(v)) return v;
  const base = Number(current);
  const b = Number.isFinite(base) ? base : (v.op === "*" ? 1 : 0);
  return v.op === "*" ? b * v.by : b + v.by;
}

/**
 * The args of a line that are relative, resolved: `currentOf(key)` is the
 * value now (or undefined), `defaults[key]` what counts as now when there
 * is none. Returns a copy with plain values.
 */
export function resolveRelArgs(args, currentOf, defaults = {}) {
  return args.map(a => {
    if (!isRel(a.value)) return a;
    const now = currentOf(a.type);
    return { ...a, value: applyRel(now ?? defaults[a.type], a.value) };
  });
}

/** Any relative value left in an AST's args (deep in objects too). */
export function hasRel(v) {
  if (isRel(v)) return true;
  if (Array.isArray(v)) return v.some(hasRel);
  if (v && typeof v === "object") return Object.values(v).some(hasRel);
  return false;
}
